import type {Breadcrumb, ErrorEvent, StackFrame} from "@sentry/react";
import {normalizeDiagnosticRoute} from "./networkDiagnostics";

const SAFE_TYPES = new Set([
  "Error",
  "TypeError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "URIError",
  "EvalError",
  "AxiosError",
  "AbortError",
  "TimeoutError",
  "NetworkError",
]);
const SAFE_MESSAGES = new Set([
  "HTTP failure",
  "Request timeout",
  "Request cancelled",
  "Network failure",
  "Browser operation failed",
]);
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

export function sanitizeDiagnosticContext(input: Record<string, unknown> = {}) {
  const result: Record<string, string | number | boolean> = {};
  for (const key of ["operationId", "attemptId"]) {
    if (typeof input[key] === "string" && UUID.test(input[key])) {
      result[key] = input[key];
    }
  }
  for (const key of ["fileSize", "durationMs", "timeoutMs", "status"]) {
    if (typeof input[key] === "number" && Number.isFinite(input[key]) && input[key] >= 0) {
      result[key] = input[key];
    }
  }
  const enums = {
    method: ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"],
    visibility: ["visible", "hidden", "prerender", "unknown"],
    classification: ["http", "timeout", "cancelled", "network", "local"],
    outcome: ["success", "failure", "sent", "failed", "not_requested", "skipped"],
    code: [
      "ERR_NETWORK",
      "ETIMEDOUT",
      "ECONNABORTED",
      "ERR_CANCELED",
      "ERR_BAD_REQUEST",
      "ERR_BAD_RESPONSE",
    ],
    step: ["read", "save", "generate", "upload", "download", "email"],
    fileType: [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/octet-stream",
    ],
  };
  for (const [key, values] of Object.entries(enums)) {
    if (typeof input[key] === "string" && values.includes(input[key])) {
      result[key] = input[key];
    }
  }
  if (typeof input.online === "boolean") {
    result.online = input.online;
  }
  if (
    typeof input.startedAt === "string" &&
    /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/.test(input.startedAt)
  ) {
    result.startedAt = input.startedAt;
  }
  if (typeof input.route === "string") {
    result.route = normalizeDiagnosticRoute(input.route);
  }
  return result;
}

function sanitizeFrame(frame: StackFrame): StackFrame {
  let filename = "[script]";
  try {
    const url = new URL(frame.filename ?? "", window.location.origin);
    if (
      url.origin === window.location.origin &&
      /^\/assets\/[a-zA-Z0-9_.-]+\.js$/.test(url.pathname)
    ) {
      filename = `${url.origin}${url.pathname}`;
    }
  } catch {}
  return {
    filename,
    function: /^[a-zA-Z0-9_.$<> ]{1,100}$/.test(frame.function ?? "") ? frame.function : undefined,
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  };
}

export function sanitizeBrowserBreadcrumb(crumb: Breadcrumb): Breadcrumb | null {
  const base = {timestamp: crumb.timestamp, level: crumb.level};
  if (["diagnostic", "diagnostic.step", "diagnostic.request"].includes(crumb.category ?? "")) {
    return {...base, category: crumb.category, data: sanitizeDiagnosticContext(crumb.data)};
  }
  if (crumb.type === "http" || ["xhr", "fetch"].includes(crumb.category ?? "")) {
    return {
      ...base,
      category: "http",
      type: "http",
      data: sanitizeDiagnosticContext({
        route: crumb.data?.route ?? crumb.data?.url,
        method: crumb.data?.method,
        status: crumb.data?.status ?? crumb.data?.status_code,
      }),
    };
  }
  if (crumb.category === "navigation") {
    return {...base, category: "navigation"};
  }
  return null;
}

export function sanitizeBrowserEvent(event: ErrorEvent): ErrorEvent {
  const source = event.tags?.source;
  const managed = event.tags?.["diagnostic.managed"] === "true";
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    level: event.level,
    environment: event.environment,
    release: event.release,
    sdk: event.sdk,
    exception: event.exception
      ? {
          values: event.exception.values?.map((exception) => ({
            type: SAFE_TYPES.has(exception.type ?? "") ? exception.type : "Error",
            value:
              managed && SAFE_MESSAGES.has(exception.value ?? "")
                ? exception.value
                : "Browser operation failed",
            stacktrace: exception.stacktrace
              ? {frames: exception.stacktrace.frames?.map(sanitizeFrame)}
              : undefined,
            mechanism: exception.mechanism
              ? {type: "generic", handled: exception.mechanism.handled}
              : undefined,
          })),
        }
      : undefined,
    message: event.message ? "Browser operation failed" : undefined,
    user: event.user ? {id: event.user.id, email: event.user.email} : undefined,
    tags: {
      ...(managed ? {"diagnostic.managed": "true"} : {}),
      ...(typeof source === "string" &&
      /^(?:react-query-(?:query|mutation)|document-upload|certificate-action|browser-action|attestation-save|attestation-download)$/.test(
        source
      )
        ? {source}
        : {}),
    },
    contexts: {diagnostic: sanitizeDiagnosticContext(event.contexts?.diagnostic)},
    breadcrumbs: event.breadcrumbs
      ?.map(sanitizeBrowserBreadcrumb)
      .filter((crumb) => crumb !== null),
  };
}
